import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form'; // For form handling
import { motion, AnimatePresence } from 'framer-motion'; // For animations

// Import CSS with prefix
import './Contact.css';
import BranchLocator from '../Ourbranches/Ourbranches';

// --- Placeholder Icons (MUST REPLACE with actual icons) ---
const PlaceholderIcon = ({ children, className = "" }) => <span className={`contact-icon-placeholder ${className}`}>{children}</span>;
const MapMarkerIcon = () => <PlaceholderIcon className="icon-map">📍</PlaceholderIcon>;
const BuildingIcon = () => <PlaceholderIcon className="icon-building">🏢</PlaceholderIcon>;
const PhoneIcon = () => <PlaceholderIcon className="icon-phone">📞</PlaceholderIcon>;
const EmailIcon = () => <PlaceholderIcon className="icon-email">✉️</PlaceholderIcon>;
const WhatsappIcon = () => <PlaceholderIcon className="icon-whatsapp">💬</PlaceholderIcon>;
const UserIcon = () => <PlaceholderIcon className="icon-user">👤</PlaceholderIcon>;
const AtIcon = () => <PlaceholderIcon className="icon-at">@</PlaceholderIcon>;
const MobileIcon = () => <PlaceholderIcon className="icon-mobile">📱</PlaceholderIcon>;
const SubjectIcon = () => <PlaceholderIcon className="icon-subject">📋</PlaceholderIcon>;
const MessageIcon = () => <PlaceholderIcon className="icon-message">✏️</PlaceholderIcon>;
const SpinnerIcon = () => <div className="contact-spinner"></div>; // Custom Spinner CSS
const SendIcon = () => ( // Refined Send Icon (Example SVG)
    <svg className="contact-send-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
    </svg>
);
const ErrorIcon = () => <PlaceholderIcon className="icon-error">⚠️</PlaceholderIcon>;
const SuccessIcon = () => <PlaceholderIcon className="icon-success">✓</PlaceholderIcon>;
const RobotIcon = () => <PlaceholderIcon className="icon-robot">🤖</PlaceholderIcon>; // Keep if Chatbot used
const CloseIcon = () => <PlaceholderIcon className="icon-close">✕</PlaceholderIcon>; // Keep if Chatbot used


// --- Reusable Intersection Observer Hook ---
// (Keep the hook definition from the previous version)
const useVisibility = (options) => {
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (options?.triggerOnce && observerRef.current) {
                        observerRef.current.unobserve(currentElement);
                    }
                } else {
                    if (options && !options.triggerOnce) {
                        setIsVisible(false);
                    }
                }
            });
        }, options);
        observerRef.current.observe(currentElement);
        return () => { if (observerRef.current && currentElement) observerRef.current.unobserve(currentElement); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options?.threshold, options?.rootMargin, options?.triggerOnce]);

    return [elementRef, isVisible];
};


// --- Helper for character animation ---
// (Keep the CharAnimator definition from the previous version)
const CharAnimator = ({ text, baseDelay = 0, stagger = 0.03 }) => {
    if (!text || typeof text !== 'string' || text.length === 0) return null;
    return text.split('').map((char, index) => (
        <span key={index} className="contact-char-reveal" style={{ '--char-delay': `${baseDelay + index * stagger}s` }} aria-hidden="true">
            {char === ' ' ? '\u00A0' : char}
        </span>
    ));
};


// --- FloatingLabelInput Component (Internal - Enhanced) ---
const FloatingLabelInput = ({ id, label, type = 'text', register, errors, validationOptions, isTextarea = false, disabled, icon: Icon, ...props }) => {
    const [hasValue, setHasValue] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const fieldError = errors && errors[id];
    const registeredProps = register ? register(id, validationOptions) : {};
    const inputRef = useRef(null);

    useEffect(() => {
        // Initialize based on registered value or direct value prop
        const initialValue = registeredProps?.value || props?.value || inputRef.current?.value;
        if (initialValue) {
            setHasValue(true);
        }
    }, [registeredProps?.value, props?.value]); // Depend on values

    const handleInputChange = (e) => {
        setHasValue(e.target.value !== '');
        registeredProps.onChange?.(e);
    };
    const handleFocus = () => setIsFocused(true);
    const handleBlur = (e) => {
        setIsFocused(false);
        setHasValue(e.target.value !== '');
        registeredProps.onBlur?.(e);
    };

    const isLabelActive = isFocused || hasValue;
    const InputComponent = isTextarea ? 'textarea' : 'input';
    const hasIcon = !!Icon;

    const combinedRef = (el) => {
        if (typeof registeredProps.ref === 'function') {
            registeredProps.ref(el);
        }
        inputRef.current = el;
    };

    const errorId = `${id}-error-${React.useId()}`;

    // Add focused class to wrapper for CSS targeting
    const wrapperClass = `contact-input-wrapper ${hasIcon ? 'has-icon' : ''} ${fieldError ? 'has-error' : ''} ${isLabelActive ? 'is-active' : ''} ${isFocused ? 'is-focused' : ''}`;

    return (
        <div className={wrapperClass}>
            {Icon && <div className="contact-input-icon"><Icon /></div>}
            <InputComponent
                id={id}
                type={type}
                placeholder=" " // Needs placeholder for label animation trigger
                {...registeredProps}
                ref={combinedRef}
                {...props}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className="contact-input-field"
                aria-invalid={fieldError ? "true" : "false"}
                aria-describedby={fieldError ? errorId : undefined}
                disabled={disabled}
            />
            {/* Label comes *after* input for some CSS selectors */}
            <label htmlFor={id} className="contact-input-label">
                {label} {validationOptions?.required && <span className="contact-required-asterisk">*</span>}
            </label>
            {/* Animated effects div */}
            <div className="contact-input-fx-container">
                <div className="contact-input-fx-border"></div>
                <div className="contact-input-fx-glow"></div>
            </div>
            <AnimatePresence>
                {fieldError && (
                    <motion.p
                        id={errorId}
                        className="contact-error-message"
                        role="alert"
                        initial={{ opacity: 0, y: -5, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto', marginTop: '0.5rem' }}
                        exit={{ opacity: 0, y: -5, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <ErrorIcon /> {fieldError.message}
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
};


// --- Contact Form Component (Internal - Enhanced) ---
const ContactForm = () => {
    const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({ mode: 'onBlur' });
    const [submitStatus, setSubmitStatus] = useState({ message: '', type: '' });
    const formRef = useRef(null);

    // Debounce and label reset logic (keep from previous version)
    const debounce = useCallback((func, delay) => { /* ... keep implementation ... */
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
     }, []);
    const triggerLabelReset = useCallback(debounce(() => { /* ... keep implementation ... */
        if (formRef.current) {
            const inputs = formRef.current.querySelectorAll('input, textarea');
            inputs.forEach(input => {
                 const wrapper = input.closest('.contact-input-wrapper');
                 if (wrapper) {
                     if (input.value) wrapper.classList.add('is-active');
                     else wrapper.classList.remove('is-active');
                 }
            });
        }
     }, 50), [debounce]);

    const onSubmit = async (data) => {
        setSubmitStatus({ message: '', type: '' });
        console.log('Submitting Data:', data);

        // --- SIMULATED API CALL ---
        await new Promise(resolve => setTimeout(resolve, 2200)); // Longer simulation
        const success = Math.random() > 0.1; // Higher success rate for demo
        // --- END SIMULATION ---

        if (success) {
            setSubmitStatus({ message: 'Message Sent! Thank you for reaching out.', type: 'success' });
            reset();
            triggerLabelReset();
        } else {
            setSubmitStatus({ message: 'Submission failed. Please try again later.', type: 'error' });
        }
    };

    // Animation variants for the form container
    const formContainerVariants = {
        hidden: { opacity: 0, y: 60, scale: 0.98 },
        visible: {
            opacity: 1, y: 0, scale: 1,
            transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], staggerChildren: 0.08 } // Stagger children slightly
        }
    };
    // Variants for individual input fields within the form
     const inputVariants = {
         hidden: { opacity: 0, x: -20 },
         visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
     };

    return (
        <motion.div
            className="contact-form-container"
            variants={formContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
        >
             {/* Animated background layers for the form container */}
             <div className="contact-form-bg-layer layer-1"></div>
             <div className="contact-form-bg-layer layer-2"></div>
             <div className="contact-form-bg-layer layer-3"></div>

            <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate className="contact-form">
                {/* Form title */}
                <div className="contact-heading-mask">
                    <h3 className="contact-form-title">
                        Let's Connect
                    </h3>
                </div>

                {/* Wrap each input in motion.div for staggered animation */}
                <motion.div variants={inputVariants}>
                    <FloatingLabelInput id="fullName" label="Full Name" icon={UserIcon} register={register} errors={errors} validationOptions={{ required: 'Full Name is required' }} disabled={isSubmitting} />
                </motion.div>
                <motion.div variants={inputVariants}>
                    <FloatingLabelInput id="email" label="Email Address" type="email" icon={AtIcon} register={register} errors={errors} validationOptions={{ required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' }}} disabled={isSubmitting} />
                 </motion.div>
                <motion.div variants={inputVariants}>
                    <FloatingLabelInput id="phone" label="Phone (Optional)" type="tel" icon={MobileIcon} register={register} errors={errors} disabled={isSubmitting} />
                 </motion.div>
                <motion.div variants={inputVariants}>
                    <FloatingLabelInput id="subject" label="Subject / Position" icon={SubjectIcon} register={register} errors={errors} validationOptions={{ required: 'Subject is required' }} disabled={isSubmitting} />
                 </motion.div>
                <motion.div variants={inputVariants}>
                    <FloatingLabelInput id="message" label="Your Message" icon={MessageIcon} isTextarea={true} register={register} errors={errors} validationOptions={{ required: 'Message is required', minLength: { value: 15, message: "Message too short (min 15 chars)"} }} disabled={isSubmitting} rows={5} />
                 </motion.div>

                {/* Submit Button */}
                 <motion.div variants={inputVariants} style={{ display: 'flex', justifyContent: 'center' }}> {/* Wrapper for centering and animation */}
                    <motion.button
                        type="submit"
                        className="contact-submit-button"
                        disabled={isSubmitting}
                        whileHover={!isSubmitting ? { scale: 1.04, y: -5, /* Add more effects */ } : {}}
                        whileTap={!isSubmitting ? { scale: 0.96, y: -1 } : {}}
                        transition={{ type: 'spring', stiffness: 350, damping: 18 }} // Springy transition
                    >
                        {/* Multiple spans for layered text/icon effects */}
                         <span className="contact-button-icon-wrapper">
                            {isSubmitting ? <SpinnerIcon /> : <SendIcon />}
                         </span>
                         <span className="contact-button-text-wrapper">
                            <span className="contact-button-text-original">
                                {isSubmitting ? "Sending..." : "Submit Inquiry"}
                            </span>
                           
                        </span>
                        {/* Effect Layers */}
                        <span className="contact-submit-button-border-fx"></span>
                        <span className="contact-submit-button-flare"></span>
                        <span className="contact-submit-button-particles">
                            {/* Generate particle spans dynamically */}
                            {[...Array(15)].map((_, i) => <span key={i} style={{ '--particle-index': i }}></span>)}
                        </span>
                    </motion.button>
                 </motion.div>

                {/* Feedback Message Area */}
                <AnimatePresence>
                    {submitStatus.message && (
                        <motion.div
                            className={`contact-form-feedback ${submitStatus.type}`}
                            role={submitStatus.type === 'error' ? 'alert' : 'status'}
                            aria-live="polite"
                            initial={{ opacity: 0, y: 15, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                        >
                           {submitStatus.type === 'success' && <SuccessIcon/> }
                           {submitStatus.type === 'error' && <ErrorIcon/> }
                           <span>{submitStatus.message}</span> {/* Wrap message */}
                        </motion.div>
                    )}
                </AnimatePresence>
            </form>
        </motion.div>
    );
};


// --- Address Card Component (Internal - Keep enhanced version) ---
const AddressCard = ({ title, addressLines = [], icon: Icon = BuildingIcon, index }) => {
    const [cardRef, isCardVisible] = useVisibility({threshold: 0.2, triggerOnce: true});
    return (
        <div ref={cardRef} className={`contact-address-card ${isCardVisible ? 'is-visible' : ''}`} style={{ '--card-delay': `${index * 0.15}s` }}>
            {/* Keep structure from Apex v6 */}
            <div className="contact-address-card-inner">
                <div className="contact-address-card-bg"></div>
                <div className="contact-address-icon-wrapper"><Icon /></div>
                <div className="contact-address-content">
                    <h3>{title}</h3>
                    <address>{addressLines.map((line, idx) => (<p key={idx}>{line}</p>))}</address>
                </div>
                <div className="contact-address-card-border-glow"></div>
            </div>
        </div>
    );
};

// --- Direct Contact Item (Internal - Keep enhanced version) ---
const DirectContactItem = ({ title, text, link, icon: Icon, index, linkType = "link"}) => {
    const [itemRef, isItemVisible] = useVisibility({threshold: 0.2, triggerOnce: true});
    const linkProps = linkType === 'tel' ? { href: `tel:${link}` }
                    : linkType === 'mailto' ? { href: `mailto:${link}` }
                    : { href: link, target: "_blank", rel: "noopener noreferrer" };

    return (
        <div ref={itemRef} className={`contact-direct-item ${isItemVisible ? 'is-visible' : ''}`} style={{ '--item-delay': `${index * 0.15}s` }}>
             {/* Keep structure from Apex v6 */}
             <div className="contact-direct-item-bg"></div>
             <div className="contact-direct-icon-wrapper"><Icon/></div>
             <div className="contact-direct-content">
                 <h4>{title}</h4><br/>
                 {link ? (<a {...linkProps}>{text}</a>) : (<p>{text}</p>)}
             </div>
             <div className="contact-direct-item-shine"></div>
        </div>
    );
};


// --- ContactPage Component (Main Structure) ---
const ContactPage = () => {
  // --- <<< IMPORTANT: REPLACE PLACEHOLDERS >>> ---

  const primaryEmail = "cliniqorindia@gmail.com"; // *** REPLACE ***
  const mainMapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30269.591111715054!2d73.91881926569624!3d18.4972935845656!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2e9ff81f1aae9%3A0x2560343555ac8b53!2sHadapsar%2C%20Pune%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1746258250113!5m2!1sen!2sin"; // *** REPLACE ***
  const officeAddresses = [
      { title: "Registered Office", lines: [" Pune,", "Maharashtra - 411028, India"], icon: BuildingIcon },
      { title: "Corporate Office", lines: [" Mumbai,", "Maharashtra - 400001, India"], icon: BuildingIcon },
    
  ];
  const branchAddresses = [
      { title: "Goa ",  icon: BuildingIcon },
      { title: "Bihar ",  icon: BuildingIcon },
    { title: "Pune ",  icon: BuildingIcon },
    { title: "Delhi ",  icon: BuildingIcon },
    { title: "Mumbai ",  icon: BuildingIcon }, 
    { title: "Dehradun ",  icon: BuildingIcon },
    { title: "Hydrabad ",  icon: BuildingIcon },
    { title: "Bengaluru ",  icon: BuildingIcon },
    { title: "Kolkata ",  icon: BuildingIcon },
    { title: "Uttar Pradesh ",  icon: BuildingIcon },
  
];
  // --- <<< END OF PLACEHOLDERS >>> ---

  // Observer refs
  const [headerRef, isHeaderVisible] = useVisibility({ threshold: 0.3, triggerOnce: true });
  const [mapRef, isMapVisible] = useVisibility({ threshold: 0.1, triggerOnce: true });
  const [contactInfoRef, isContactInfoVisible] = useVisibility({ threshold: 0.05, triggerOnce: true });
  const [directContactRef, isDirectContactVisible] = useVisibility({ threshold: 0.05, triggerOnce: true });


  return (
       <div className="contact-page-container">

         {/* Apex Hero Section */}
         <header ref={headerRef} className={`contact-hero-section ${isHeaderVisible ? 'is-visible' : ''}`}>
            {/* Keep header elements */}
            <div className="contact-hero-bg-elements">
                <div className="contact-hero-shape shape-1"></div>
                <div className="contact-hero-shape shape-2"></div>
                <div className="contact-hero-shape shape-3"></div>
            </div>
            <div className="contact-hero-content">
                 <h1 className="contact-hero-title" aria-label="Get In Touch">
                     <CharAnimator text="Get In Touch" baseDelay={0.2} stagger={0.05}/>
                 </h1>
                 <p className="contact-hero-subtitle">
                    Connect with us today. We're ready to assist with all your healthcare staffing and project needs.
                 </p>
             </div>
         </header>

         {/* Main Content Grid */}
         <main className="contact-main-content">
            <div className="contact-content-grid">
                {/* Form Column */}
                <div className="contact-form-column">
                    {/* ContactForm animation handled internally */}
                    <ContactForm />
                </div>

                 {/* Info Column */}
                 <div className="contact-info-column">
                    {/* Direct Contact Methods */}
                    <section ref={directContactRef} className={`contact-direct-section ${isDirectContactVisible ? 'is-visible' : ''}`}>
                         {/* Add heading animation */}
                         <div className="contact-heading-mask">
                             <h4>Direct Lines</h4>
                         </div>
                         <div className="contact-direct-grid">
                            {/* Pass index for stagger delay */}
                           
                            <DirectContactItem index={3} title="General Email"
                             text={primaryEmail} link={primaryEmail} icon={EmailIcon} linkType="mailto" />
                        </div>
                    </section>

                    {/* Office Addresses */}
                    <section ref={contactInfoRef} className={`contact-info-section ${isContactInfoVisible ? 'is-visible' : ''}`}>
                         <div className="contact-heading-mask">
                             <h4>Our Offices</h4>
                         </div>
                        <div className="contact-address-grid">
                            {officeAddresses.map((addr, index) => (
                                <AddressCard
                                    key={index}
                                    title={addr.title}
                                    addressLines={addr.lines}
                                    icon={addr.icon}
                                    index={index} // Pass index for stagger
                                />
                            ))}
                        </div>
                    </section>
                </div>
            </div>
            {/* <section ref={contactInfoRef} className={`contact-info-section ${isContactInfoVisible ? 'is-visible' : ''}`}>
                         <div className="contact-heading-mask">
                             <h4>Our Branches</h4>
                         </div>
                        <div className="contact-address-grid" style={{display:"flex"}}>
                            {branchAddresses.map((addr, index) => (
                                <AddressCard
                                    key={index}
                                    title={addr.title}
                                    
                                    icon={addr.icon}
                                    index={index} // Pass index for stagger
                                />
                            ))}
                        </div>
                    </section> */}
                    <BranchLocator/>
         </main>

         {/* Full Width Map Section */}
         <section ref={mapRef} className={`contact-map-section ${isMapVisible ? 'is-visible' : ''}`}>
              <div className="contact-map-embed-container">
                  <iframe
                      src={mainMapEmbedUrl}
                      width="100%" height="100%" style={{ border: 0 }}
                      allowFullScreen={true} loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Cliniqor Health Care Office Location Map"
                  ></iframe>
               </div>
          </section>

         {/* Chatbot could be added here */}

       </div> // End contact-page-container
  );
};

export default ContactPage; // Export the main page component